import type React from "react";
import { useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import invariant from "ts-invariant";

import { useGetManagerServices } from "api/services/appointment/hooks";
import type { HcpServiceManager } from "api/services/appointment/schemas/hcp_service";
import { useProfileContext } from "contexts/ProfileContext";
import { ServiceForm, type ServiceFormRef } from "routes/calendar/components/ServiceForm";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { View } from "shared/containers/View";
import type { Breadcrumb } from "shared/containers/View/components/ViewBreadcrumbs";

export type FormStates = "default" | "saving" | "success" | "dirty";
type FormMode = "create" | "edit";

export const ServicesFormView: React.FC = () => {
  const navigate = useNavigate();
  const [formLoading, setFormLoading] = useState(false);
  const { serviceId } = useParams();
  const { profile } = useProfileContext();
  const { t } = useTranslation();

  const formRef = useRef<ServiceFormRef>(null);

  const handleSave = async () => {
    await formRef.current?.submitForm();
  };

  const formMode: FormMode = serviceId ? "edit" : "create";

  invariant(profile);

  const { data: services = [], isError, isLoading } = useGetManagerServices(profile.id);
  const service: HcpServiceManager | undefined = services.find(el => el.id === Number(serviceId));
  const title =
    formMode === "create" ? t("booking.services.new_service") : `${t("common.edit")} ${service?.name || ""}`;

  const additionalBreadcrumbs: Breadcrumb[] = [];
  if (service) {
    additionalBreadcrumbs.push({
      label: service.name,
      href: `/calendar/services/edit/${service.id}`,
    });
  } else {
    additionalBreadcrumbs.push({
      label: t("booking.services.new_service"),
      href: `/calendar/services/new`,
    });
  }

  const handleFormDone = () => {
    navigate("/calendar/services");
  };

  const disableActionButtons = formLoading || isLoading;

  return (
    <View
      title={title}
      isLoading={isLoading}
      actionButtons={[
        <BaseButton
          text={t("buttons.cancel")}
          onClick={handleFormDone}
          variant="tertiary"
          disabled={disableActionButtons}
          size="large"
        />,
        <BaseButton
          text={formMode === "edit" ? t("buttons.update") : t("buttons.create")}
          onClick={handleSave}
          disabled={disableActionButtons}
          size="large"
        />,
      ]}
      additionalBreadcrumbs={additionalBreadcrumbs}
    >
      {isError ? (
        <Notification variant="danger">{t("errors.generic")}</Notification>
      ) : (
        <ServiceForm ref={formRef} service={service} onLoading={setFormLoading} onSuccess={handleFormDone} />
      )}
    </View>
  );
};
